"use client"

import { useState, useEffect, useCallback } from "react"
import { useWallet } from "@/lib/wallet-provider"
import { signedFetch } from "@/lib/api-auth-client"

export const SATS_PER_CRYSTAL = 100

interface SpendResult {
  ok: boolean
  balance: number
  error?: string
}

/**
 * Crystal balance + purchase/spend flows.
 *
 * Crystals are an off-chain balance (Turso) bought with a single BRC-29
 * payment to the platform key. In-run purchases (premium towers, etc.) spend
 * crystals so no wallet prompt interrupts combat.
 */
export function useCrystals() {
  const { status, identityKey, pay, paying } = useWallet()
  const [balance, setBalance] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!identityKey) {
      setBalance(0)
      return
    }
    setIsLoading(true)
    try {
      const res = await fetch(`/api/crystals?pubkey=${encodeURIComponent(identityKey)}`)
      if (res.ok) {
        const data = await res.json()
        setBalance(typeof data.balance === "number" ? data.balance : 0)
      }
    } catch {
      // Keep the last known balance on transient failures
    } finally {
      setIsLoading(false)
    }
  }, [identityKey])

  useEffect(() => {
    refresh()
  }, [refresh])

  /**
   * Buy crystals with one BRC-29 payment to the platform key.
   * Returns { ok, error? }; refreshes the balance on success.
   */
  const buyCrystals = useCallback(
    async (crystals: number): Promise<{ ok: boolean; error?: string }> => {
      const result = await pay({
        satoshis: crystals * SATS_PER_CRYSTAL,
        description: `Pelf: ${crystals} crystals`,
        endpoint: "/api/crystals/purchase",
        purpose: "crystals",
      })
      if (!result.ok) {
        // Prefer the wallet's own error message - "Payment failed" alone
        // hides the actual cause (insufficient funds, denied approval, ...)
        return { ok: false, error: result.error || "Payment failed" }
      }
      // Server credit may lag the durable queue slightly; refresh twice
      await refresh()
      setTimeout(() => refresh(), 2500)
      return { ok: true }
    },
    [pay, refresh],
  )

  /** Spend crystals server-side. Returns { ok, balance }. */
  const spendCrystals = useCallback(
    async (amount: number, reason: string): Promise<SpendResult> => {
      if (!identityKey) return { ok: false, balance: 0, error: "Not connected" }
      try {
        // BRC-100-signed request: the server verifies the wallet signature and
        // uses the signed pubkey as the spender, so identity can't be spoofed.
        const res = await signedFetch("/api/crystals/spend", { amount, reason }, identityKey)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) {
          return {
            ok: false,
            balance: typeof data.balance === "number" ? data.balance : balance,
            error: data.error || (res.status === 402 ? "Not enough crystals" : "Spend failed"),
          }
        }
        const newBalance = typeof data.balance === "number" ? data.balance : balance
        setBalance(newBalance)
        return { ok: true, balance: newBalance }
      } catch {
        // Covers both network failures and the wallet declining to sign
        return { ok: false, balance, error: "Network error" }
      }
    },
    [identityKey, balance],
  )

  return {
    balance,
    isLoading,
    isPurchasing: paying,
    isConnected: status === "connected",
    refresh,
    buyCrystals,
    spendCrystals,
  }
}
